import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useShop } from './ShopContext';
import { formatApiErrorDetail } from '../utils/apiError';

const API_URL = process.env.REACT_APP_API_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const { merchantId } = useShop();

    const TOKEN_KEY = `instashop_customer_token_${merchantId || 'default'}`;
    const USER_KEY = `instashop_customer_user_${merchantId || 'default'}`;

    // Load saved auth when merchantId is available
    useEffect(() => {
        if (!merchantId) return;
        const savedToken = localStorage.getItem(TOKEN_KEY);
        const savedUser = localStorage.getItem(USER_KEY);
        
        if (savedToken && savedUser) {
            try {
                setToken(savedToken);
                setUser(JSON.parse(savedUser));
            } catch (e) {
                console.error('Failed to parse saved user:', e);
            }
        }
        setLoading(false);
    }, [merchantId, TOKEN_KEY, USER_KEY]);

    // Get auth headers for API requests
    const getAuthHeaders = useCallback(() => {
        if (!token) return {};
        return { Authorization: `Bearer ${token}` };
    }, [token]);

    // Login customer
    const login = async (email, password) => {
        try {
            const response = await axios.post(`${API_URL}/storefront/auth/login`, {
                email,
                password,
                merchant_id: merchantId
            });
            
            const { access_token, customer } = response.data;
            
            setToken(access_token);
            setUser(customer);
            localStorage.setItem(TOKEN_KEY, access_token);
            localStorage.setItem(USER_KEY, JSON.stringify(customer));
            
            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: formatApiErrorDetail(error.response?.data?.detail, 'Αποτυχία σύνδεσης'),
            };
        }
    };

    // Register customer
    const register = async (name, email, password, subscribeNewsletter = false) => {
        try {
            const response = await axios.post(`${API_URL}/storefront/auth/register`, {
                name,
                email,
                password,
                merchant_id: merchantId,
                subscribe_newsletter: subscribeNewsletter
            });
            
            const { access_token, customer } = response.data;
            
            setToken(access_token);
            setUser(customer);
            localStorage.setItem(TOKEN_KEY, access_token);
            localStorage.setItem(USER_KEY, JSON.stringify(customer));
            
            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: formatApiErrorDetail(error.response?.data?.detail, 'Αποτυχία εγγραφής'),
            };
        }
    };

    // Logout customer
    const logout = () => {
        setToken(null);
        setUser(null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
    };

    const syncCustomerFromApi = useCallback(async () => {
        if (!token) return;
        try {
            const { data } = await axios.get(`${API_URL}/storefront/auth/me`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUser(data);
            localStorage.setItem(USER_KEY, JSON.stringify(data));
        } catch {
            /* ignore */
        }
    }, [token, USER_KEY]);

    const resendVerificationEmail = async () => {
        try {
            await axios.post(
                `${API_URL}/storefront/auth/resend-verification`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: formatApiErrorDetail(error.response?.data?.detail, 'Αποτυχία αποστολής email'),
            };
        }
    };

    const value = {
        token,
        user,
        loading,
        login,
        register,
        logout,
        getAuthHeaders,
        isAuthenticated: !!token,
        resendVerificationEmail,
        syncCustomerFromApi,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
